import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import CommonSelect from "../../../core/common/commonSelect";
import TicketListModal, {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
} from "../../../core/modals/ticketListModal";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders, getAPIMultiPartHeaders } from "../../../common/Utils";
import "./tickets.css";

// Define interfaces
interface Comment {
  id: string;
  ticketId: string;
  messageText: string;
  createdBy: string;
  senderFullName: string;
  createdOn: string;
  senderProfileImageUrl: string;
  files?: Attachment[];
}

interface Attachment {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  ticketId?: string;
  commentId?: string;
}

interface TicketDetail {
  id: string;
  accountId: number;
  ticketNumber: string;
  title: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  createdBy: string;
  username: string;
  createdOn: string;
  updatedOn: string;
  userProfileImageUrl: string;
  assignedTo: string;
  assignedToUsername: string;
  assignedToUserProfileImageUrl: string;
  supportAgentId?: string;
  supportAgentName?: string;
  supportAgentProfileImageUrl?: string;
  ticketConversations: Comment[];
  files: Attachment[];
  createdByEmail: string;
  lastUpdatedOn: string;
}

// Add interface for reply form
interface ReplyForm {
  content: string;
  files: File[];
}

const TicketDetails = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const { callFetch, isLoading, error } = useFetch();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Reply form state
  const [replyForm, setReplyForm] = useState<ReplyForm>({
    content: "",
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);

  // Toast notification state
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<
    "success" | "danger" | "warning" | "info"
  >("success");

  // Define options for selects
  const changePriority = PRIORITY_OPTIONS;

  const assignTo = [{ value: "Sanchalak Support", label: "Sanchalak Support" }];

  const ticketStatus = STATUS_OPTIONS;

  // Fetch ticket details
  useEffect(() => {
    if (id) {
      fetchTicketDetails(id);
    }
  }, [id]);

  const fetchTicketDetails = async (ticketId: string) => {
    setRefreshing(true);
    try {
      const url = API.GET_SUPPORT_TICKET_DETAILS.replace(":ticketId", ticketId);
      const response = await callFetch(url, "GET", getAPIHeaders(), null, null);
      if (response) {
        setTicket(response);
      }
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      setRefreshing(false);
    }
  };

  // Function to update ticket priority or status
  const updateTicket = async (
    ticketId: string,
    field: "priority" | "status" | "assignedTo",
    value: string
  ) => {
    try {
      // Create proper JSON payload with the field to update
      const payload = {
        id: ticketId,
        [field]: value,
      };

      await callFetch(
        API.UPDATE_SUPPORT_TICKET,
        "PUT",
        getAPIHeaders(),
        null,
        payload
      );

      // Update the local state to reflect the change
      if (ticket) {
        setTicket({
          ...ticket,
          [field]: value,
          updatedOn: new Date().toISOString(),
        });
      }

      // Show success toast notification
      setToastMessage(`Ticket ${field} updated successfully`);
      setToastType("success");
      setShowToast(true);

      // Auto-hide toast after 2 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
    } catch (error) {
      console.error(`Error updating ticket ${field}:`, error);

      // Show error toast notification
      setToastMessage(`Failed to update ticket ${field}`);
      setToastType("danger");
      setShowToast(true);

      // Auto-hide toast after 2 seconds
      setTimeout(() => {
        setShowToast(false);
      }, 2000);
    }
  };

  // Format date to display how long ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 3600;
    if (interval < 24) {
      return `${Math.floor(interval)} hours ago`;
    }
    interval = seconds / 86400;
    return `${Math.floor(interval)} days ago`;
  };

  // Format date to display in readable format
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle image preview modal
  const openImagePreview = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    // Add display styles to modal with React state instead of using Bootstrap's JS API
    const modal = document.getElementById("imagePreviewModal");
    if (modal) {
      modal.style.display = "block";
      modal.classList.add("show");
      document.body.classList.add("modal-open");

      // Add backdrop if it doesn't exist
      let backdrop = document.querySelector(".modal-backdrop");
      if (!backdrop) {
        backdrop = document.createElement("div");
        backdrop.className = "modal-backdrop fade show";
        document.body.appendChild(backdrop);
      }
    }
  };

  // Close image preview modal
  const closeImagePreview = () => {
    setSelectedImage(null);
    const modal = document.getElementById("imagePreviewModal");
    if (modal) {
      modal.style.display = "none";
      modal.classList.remove("show");
      document.body.classList.remove("modal-open");

      // Remove backdrop
      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) {
        document.body.removeChild(backdrop);
      }
    }
  };

  // Get file type icon
  const getFileTypeIcon = (fileType: string) => {
    if (fileType.includes("image")) {
      return "ti ti-photo";
    } else if (fileType.includes("pdf")) {
      return "ti ti-file-text";
    } else if (fileType.includes("doc") || fileType.includes("word")) {
      return "ti ti-file-description";
    } else if (fileType.includes("xls") || fileType.includes("sheet")) {
      return "ti ti-table";
    } else {
      return "ti ti-paperclip";
    }
  };

  // Check if file is an image
  const isImageFile = (fileType: string) => {
    return fileType.includes("image");
  };

  // Get short file name (truncate if too long)
  const getShortFileName = (fileName: string) => {
    if (fileName.length <= 20) return fileName;

    const extension = fileName.split(".").pop() || "";
    const name = fileName.substring(0, fileName.length - extension.length - 1);

    if (name.length <= 16) return fileName;

    return `${name.substring(0, 13)}...${extension ? "." + extension : ""}`;
  };

  // Handle reply form change
  const handleReplyContentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setReplyForm({
      ...replyForm,
      content: e.target.value,
    });
  };

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Convert FileList to array and limit to 5 files
      const filesArray = Array.from(e.target.files).slice(0, 5);

      // Check file size (10MB limit)
      const validFiles = filesArray.filter(
        (file) => file.size <= 10 * 1024 * 1024
      );

      if (validFiles.length !== filesArray.length) {
        alert("Some files exceed the 10MB size limit and were removed");
      }

      setReplyForm({
        ...replyForm,
        files: validFiles,
      });
    }
  };

  // Submit reply
  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!replyForm.content.trim()) {
      setSubmissionError("Please enter a comment");
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    setSubmissionSuccess(false);

    try {
      // Create form data for multipart request
      const formData = new FormData();

      // Add conversation data as JSON
      const conversationDTO = {
        ticketId: id,
        messageText: replyForm.content,
      };
      formData.append(
        "requestData",
        new Blob([JSON.stringify(conversationDTO)], {
          type: "application/json",
        })
      );

      // Add files if any
      if (replyForm.files.length > 0) {
        replyForm.files.forEach((file) => {
          formData.append("screenshots", file);
        });
      }

      // Make API call
      const url = API.ADD_TICKET_CONVERSATION.replace(":ticketId", id || "");
      const response = await callFetch(
        url,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      if (response) {
        // Reset form
        setReplyForm({ content: "", files: [] });
        setSubmissionSuccess(true);

        // Refresh ticket details to show the new comment
        fetchTicketDetails(id || "");

        // Close modal
        document.getElementById("reply-modal-close")?.click();
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      setSubmissionError("Failed to submit reply. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const resetReplyForm = () => {
    setReplyForm({ content: "", files: [] });
    setSubmissionError(null);
    setSubmissionSuccess(false);
  };

  // Handle refresh when a new ticket is created
  const handleTicketCreated = () => {
    window.location.reload();
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="mb-2">
              <h6 className="fw-medium d-flex align-items-center">
                <Link to={routes.tickets}>
                  <i className="ti ti-arrow-left me-2" />
                  Ticket Details
                </Link>
              </h6>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2 me-2">
                <button
                  className="btn btn-secondary d-flex align-items-center"
                  onClick={() => fetchTicketDetails(id || "")}
                  disabled={refreshing}
                >
                  <i className="ti ti-refresh me-2" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              </div>
              <div className="mb-2">
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_ticket"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Ticket
                </Link>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          {isLoading ? (
            <div className="row">
              <div className="col-xl-9 col-md-8">
                <div className="skeleton-loader">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between">
                      <div className="skeleton-line w-25 mb-0"></div>
                      <div className="skeleton-badge"></div>
                    </div>
                    <div className="card-body">
                      <div className="skeleton-line w-50 mb-2"></div>
                      <div className="skeleton-line w-75 mb-3"></div>
                      <div className="skeleton-line w-100 mb-3"></div>
                      <div className="skeleton-line w-100 mb-3"></div>
                      <div className="d-flex justify-content-between">
                        <div className="skeleton-avatar-line"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-4">
                <div className="skeleton-loader">
                  <div className="card">
                    <div className="card-header">
                      <div className="skeleton-line w-50 mb-0"></div>
                    </div>
                    <div className="card-body">
                      <div className="skeleton-line w-100 mb-3"></div>
                      <div className="skeleton-line w-100 mb-3"></div>
                      <div className="skeleton-line w-100 mb-3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              Error loading ticket details. Please try again later.
            </div>
          ) : ticket ? (
            <div className="row">
              <div className="col-xl-9 col-md-8">
                <div className="card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h5 className="text-info fw-medium">
                      {ticket.title || "Ticket Details"}
                    </h5>
                    <div className="d-flex align-items-center">
                      <span
                        className={`badge ${
                          ticket.priority === "High"
                            ? "bg-danger"
                            : ticket.priority === "Medium"
                            ? "bg-warning"
                            : "bg-success"
                        } me-3`}
                      >
                        <i className="ti ti-circle-filled fs-5 me-1" />
                        {ticket.priority}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div>
                      <div className="d-flex align-items-center justify-content-between flex-wrap border-bottom mb-3">
                        <div className="d-flex align-items-center flex-wrap">
                          <div className="mb-3">
                            <span className="badge badge-info rounded-pill mb-2">
                              Ticket# {ticket.ticketNumber || ticket.id}
                            </span>
                            <div className="d-flex align-items-center mb-2">
                              <h5 className="fw-semibold me-2">
                                {ticket.subject}
                              </h5>
                              <span
                                className={`badge ${
                                  ticket.status === "Open"
                                    ? "bg-success"
                                    : ticket.status === "Closed"
                                    ? "bg-secondary"
                                    : ticket.status === "On Hold"
                                    ? "bg-warning"
                                    : "bg-info"
                                } d-flex align-items-center ms-1`}
                              >
                                <i className="ti ti-circle-filled fs-5 me-1" />
                                {ticket.status}
                              </span>
                            </div>
                            <div className="d-flex align-items-center flex-wrap row-gap-2">
                              {ticket.assignedToUsername && (
                                <p className="d-flex align-items-center mb-0 me-2">
                                  <img
                                    src={
                                      ticket.assignedToUserProfileImageUrl ||
                                      "assets/img/profiles/user-default.png"
                                    }
                                    className="avatar avatar-xs rounded-circle me-2"
                                    alt="img"
                                    onError={(
                                      e: React.SyntheticEvent<
                                        HTMLImageElement,
                                        Event
                                      >
                                    ) => {
                                      const target =
                                        e.target as HTMLImageElement;
                                      target.onerror = null;
                                      target.src =
                                        "assets/img/profiles/user-default.png";
                                    }}
                                  />
                                  Assigned to{" "}
                                  <span className="text-dark ms-1">
                                    {ticket.assignedToUsername}
                                  </span>
                                </p>
                              )}
                              {!ticket.assignedToUsername && (
                                <p className="d-flex align-items-center mb-0 me-2">
                                  <ImageWithBasePath
                                    src={"assets/img/profiles/user-default.png"}
                                    className="avatar avatar-xs rounded-circle me-2"
                                    alt="img"
                                  />{" "}
                                  Assigned to{" "}
                                  <span className="text-dark ms-1">
                                    Sanchalak Support
                                  </span>
                                </p>
                              )}
                              <p className="d-flex align-items-center mb-0 me-2">
                                <i className="ti ti-calendar-bolt me-1" />
                                Updated{" "}
                                {formatTimeAgo(
                                  ticket.updatedOn || ticket.lastUpdatedOn
                                )}
                              </p>
                              <p className="d-flex align-items-center mb-0">
                                <i className="ti ti-message-circle-share me-1" />
                                {ticket.ticketConversations?.length || 0}{" "}
                                Comments
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <Link
                            to="#"
                            className="btn btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#reply_ticket"
                          >
                            <i className="ti ti-arrow-forward-up me-1" />
                            Post a Reply
                          </Link>
                        </div>
                      </div>

                      {/* Ticket Description */}
                      <div className="border-bottom mb-3 pb-3">
                        <div>
                          <p className="mb-3">{ticket.description}</p>

                          {/* Ticket Attachments */}
                          {ticket.files && ticket.files.length > 0 && (
                            <div className="ticket-attachments my-3">
                              <h6 className="mb-2">Attachments</h6>
                              <div className="d-flex flex-wrap gap-2">
                                {ticket.files.map((attachment, index) => (
                                  <div
                                    key={attachment.id}
                                    className="attachment-item"
                                  >
                                    <span className="badge bg-light fw-normal">
                                      <i
                                        className={`${getFileTypeIcon(
                                          attachment.fileType
                                        )} me-1`}
                                      ></i>
                                      {isImageFile(attachment.fileType) ? (
                                        <a
                                          href="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            openImagePreview(
                                              attachment.fileUrl
                                            );
                                          }}
                                        >
                                          {`Screenshot-${
                                            index + 1
                                          }${attachment.fileName.substring(
                                            attachment.fileName.lastIndexOf(".")
                                          )}` || `Attachment-${index + 1}`}
                                        </a>
                                      ) : (
                                        <>
                                          {getShortFileName(
                                            attachment.fileName
                                          )}
                                          <a
                                            href={attachment.fileUrl}
                                            download
                                            target="_blank"
                                            className="ms-1"
                                          >
                                            <i className="ti ti-download"></i>
                                          </a>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Original Poster Info */}
                        <div className="mt-4">
                          <div className="d-flex align-items-center mb-3">
                            <span className="avatar avatar-md avatar-rounded me-2 flex-shrink-0">
                              {ticket.userProfileImageUrl &&
                              ticket.userProfileImageUrl.startsWith("http") ? (
                                <img
                                  src={ticket.userProfileImageUrl}
                                  alt="User"
                                  className="rounded-circle"
                                  onError={(
                                    e: React.SyntheticEvent<
                                      HTMLImageElement,
                                      Event
                                    >
                                  ) => {
                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;
                                    target.src =
                                      "assets/img/profiles/user-default.png";
                                  }}
                                />
                              ) : (
                                <ImageWithBasePath
                                  src={
                                    ticket.userProfileImageUrl ||
                                    "assets/img/profiles/user-default.png"
                                  }
                                  alt="User"
                                  className="rounded-circle"
                                />
                              )}
                            </span>
                            <div>
                              <h6 className="fw-medium mb-1">
                                {ticket.username}
                              </h6>
                              <p>
                                <i className="ti ti-calendar-bolt me-1" />
                                {formatDate(ticket.createdOn)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Comments Section */}
                      {ticket.ticketConversations &&
                        ticket.ticketConversations.map((comment) => (
                          <div
                            key={comment.id}
                            className="border-bottom mb-3 pb-3"
                          >
                            <div>
                              <div className="d-flex align-items-center mb-3">
                                <span className="avatar avatar-md avatar-rounded me-2 flex-shrink-0">
                                  {comment.senderProfileImageUrl &&
                                  comment.senderProfileImageUrl.startsWith(
                                    "http"
                                  ) ? (
                                    <img
                                      src={comment.senderProfileImageUrl}
                                      alt="User"
                                      className="rounded-circle"
                                      onError={(
                                        e: React.SyntheticEvent<
                                          HTMLImageElement,
                                          Event
                                        >
                                      ) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.onerror = null;
                                        target.src =
                                          "assets/img/profiles/user-default.png";
                                      }}
                                    />
                                  ) : (
                                    <ImageWithBasePath
                                      src={
                                        comment.senderProfileImageUrl ||
                                        "assets/img/profiles/user-default.png"
                                      }
                                      alt="User"
                                      className="rounded-circle"
                                    />
                                  )}
                                </span>
                                <div>
                                  <h6 className="mb-1">
                                    {comment.senderFullName}
                                  </h6>
                                  <p>
                                    <i className="ti ti-calendar-bolt me-1" />
                                    {formatDate(comment.createdOn)}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <div className="mb-3">
                                  <p>{comment.messageText}</p>
                                </div>

                                {/* Comment Attachments */}
                                {comment.files && comment.files.length > 0 && (
                                  <div className="d-flex flex-wrap gap-2 mb-3">
                                    {comment.files.map((attachment, index) => (
                                      <div
                                        key={attachment.id}
                                        className="attachment-item"
                                      >
                                        <span className="badge bg-light fw-normal">
                                          <i
                                            className={`${getFileTypeIcon(
                                              attachment.fileType
                                            )} me-1`}
                                          ></i>
                                          {isImageFile(attachment.fileType) ? (
                                            <a
                                              href="#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                openImagePreview(
                                                  attachment.fileUrl
                                                );
                                              }}
                                            >
                                              {`Screenshot-${
                                                index + 1
                                              }${attachment.fileName.substring(
                                                attachment.fileName.lastIndexOf(
                                                  "."
                                                )
                                              )}` || `Attachment-${index + 1}`}
                                            </a>
                                          ) : (
                                            <>
                                              {getShortFileName(
                                                attachment.fileName
                                              )}
                                              <a
                                                href={attachment.fileUrl}
                                                download
                                                target="_blank"
                                                className="ms-1"
                                              >
                                                <i className="ti ti-download"></i>
                                              </a>
                                            </>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="d-flex align-items-center mt-3">
                                  <Link
                                    to="#"
                                    className="d-inline-flex align-items-center text-primary fw-medium me-3"
                                    data-bs-toggle="modal"
                                    data-bs-target="#reply_ticket"
                                  >
                                    <i className="ti ti-arrow-forward-up me-1" />
                                    Reply
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-4">
                <div className="card">
                  <div className="card-header p-3">
                    <h4>Ticket Details</h4>
                  </div>
                  <div className="card-body p-0">
                    <div className="border-bottom p-3">
                      <div className="mb-3">
                        <label className="form-label">Change Priority</label>
                        <CommonSelect
                          className="select"
                          options={changePriority}
                          defaultValue={
                            changePriority.find(
                              (p) => p.value === ticket.priority
                            ) || changePriority[0]
                          }
                          onChange={(value) =>
                            updateTicket(ticket.id, "priority", value)
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Assign To</label>
                        <CommonSelect
                          className="select"
                          options={assignTo}
                          defaultValue={
                            assignTo.find(
                              (a) => a.value === ticket.assignedToUsername
                            ) || assignTo[0]
                          }
                          onChange={(value) =>
                            updateTicket(ticket.id, "assignedTo", value)
                          }
                        />
                      </div>
                      <div>
                        <label className="form-label">Ticket Status</label>
                        <CommonSelect
                          className="select"
                          options={ticketStatus}
                          defaultValue={
                            ticketStatus.find(
                              (s) => s.value === ticket.status
                            ) || ticketStatus[0]
                          }
                          onChange={(value) =>
                            updateTicket(ticket.id, "status", value)
                          }
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <span className="avatar avatar-md me-2 flex-shrink-0">
                        {ticket.userProfileImageUrl &&
                        ticket.userProfileImageUrl.startsWith("http") ? (
                          <img
                            src={ticket.userProfileImageUrl}
                            alt="User"
                            className="rounded-circle"
                            onError={(
                              e: React.SyntheticEvent<HTMLImageElement, Event>
                            ) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src =
                                "assets/img/profiles/user-default.png";
                            }}
                          />
                        ) : (
                          <ImageWithBasePath
                            src={
                              ticket.userProfileImageUrl ||
                              "assets/img/profiles/user-default.png"
                            }
                            alt="User"
                            className="rounded-circle"
                          />
                        )}
                      </span>
                      <div>
                        <span className="fs-12">User</span>
                        <p className="text-dark">{ticket.username}</p>
                      </div>
                    </div>
                    {/* Support Agent Section - Show hardcoded value if not provided */}
                    <div className="d-flex align-items-center border-bottom p-3">
                      <span className="avatar avatar-md me-2 flex-shrink-0">
                        <ImageWithBasePath
                          src={
                            ticket.supportAgentProfileImageUrl ||
                            "assets/img/profiles/user-default.png"
                          }
                          className="rounded-circle"
                          alt="Support Agent"
                        />
                      </span>
                      <div>
                        <span className="fs-12">Support Agent</span>
                        <p className="text-dark">
                          {ticket.supportAgentName || "Sanchalak Support"}
                        </p>
                      </div>
                    </div>
                    <div className="p-3">
                      <span className="fs-12">Last Updated / Closed On</span>
                      <p className="text-dark">
                        {formatDate(ticket.lastUpdatedOn || ticket.updatedOn)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-info">
              No ticket found with the provided ID.
            </div>
          )}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2014 - 2025 © SmartHR.</p>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Image Preview Modal */}
      <div
        className={`modal fade image-preview-modal ${
          selectedImage ? "show" : ""
        }`}
        id="imagePreviewModal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center">
              <div className="d-flex align-items-center">
                <h5 className="modal-title me-2">Image Preview</h5>
                {selectedImage && (
                  <a
                    href={selectedImage}
                    download
                    className="btn btn-sm btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="ti ti-download me-1"></i>
                    Download
                  </a>
                )}
              </div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={closeImagePreview}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-center">
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Preview"
                  className="img-fluid"
                  style={{ maxHeight: "70vh" }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Reply Modal */}
      <div
        className="modal fade"
        id="reply_ticket"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Reply to Ticket</h5>
              <button
                type="button"
                className="btn-close"
                id="reply-modal-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetReplyForm}
              ></button>
            </div>
            <div className="modal-body">
              {submissionSuccess && (
                <div className="alert alert-success">
                  Reply submitted successfully!
                </div>
              )}
              {submissionError && (
                <div className="alert alert-danger">{submissionError}</div>
              )}
              <form onSubmit={handleSubmitReply}>
                <div className="mb-3">
                  <label className="form-label">Comment</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Enter your reply..."
                    value={replyForm.content}
                    onChange={handleReplyContentChange}
                    required
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Attachments</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    onChange={handleFileChange}
                    accept="image/*, .pdf, .doc, .docx, .xls, .xlsx, .txt"
                  />
                  <small className="text-muted">
                    Max 5 files (10MB each)
                    {replyForm.files.length > 0 && (
                      <span> - Selected {replyForm.files.length} file(s)</span>
                    )}
                  </small>
                </div>
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={resetReplyForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      "Submit Reply"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <TicketListModal onTicketCreated={handleTicketCreated} />

      {/* Toast notification */}
      {showToast && (
        <div
          className="position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1050 }}
        >
          <div
            className={`toast show align-items-center text-white bg-${toastType} border-0`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">
                <i
                  className={`me-2 ti ${
                    toastType === "success"
                      ? "ti-circle-check"
                      : toastType === "danger"
                      ? "ti-alert-circle"
                      : toastType === "warning"
                      ? "ti-alert-triangle"
                      : "ti-info-circle"
                  }`}
                ></i>
                {toastMessage}
              </div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setShowToast(false)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TicketDetails;
